import { Form, InputNumber, Tooltip } from 'antd';
import classNames from 'classnames';
import type { NamePath } from 'rc-field-form/es/interface';
import type { ReactNode } from 'react';
import { useStore } from 'zustand';

import { isPassed } from 'app/utils/dates';

import { inventoryStore } from '../../../InventoryStore';
import type { DuplicationModeData } from '../../../type';
import { DuplicationMenu } from '../../DuplicationMenu/DuplicationMenu';
import '../Cells.scss';

import type { CellIconProps } from './CellIcon';
import { CellIcon } from './CellIcon';

type EditableCellProps = {
  date: Date;
  nameField?: NamePath;
  name?: string;
  descriptionValue?: number | string | ReactNode;
  hasDuplication?: boolean;
  handleDuplicate?: (modeData: DuplicationModeData) => void;
  disabled?: boolean;
  iconParameters?: CellIconProps;
  tooltipMessage?: ReactNode;
  error?: string | undefined;
};

export const EditableCell = ({
  date,
  nameField,
  name,
  descriptionValue,
  hasDuplication,
  handleDuplicate,
  disabled = false,
  iconParameters,
  tooltipMessage,
  error,
}: EditableCellProps) => {
  const form = useStore(inventoryStore, (state) => state.form);
  const isMaxBooking = name === 'maxBooking';

  const formTimePeriod = useStore(
    inventoryStore,
    (state) => state.formTimePeriod
  );
  const handleDuplicateHover = useStore(
    inventoryStore,
    (state) => state.handleDuplicateHover
  );
  const dropDuplicationHover = useStore(
    inventoryStore,
    (state) => state.dropDuplicationHover
  );

  return (
    <div
      className={classNames('inventory-cell', 'edit-mode', {
        hasDescription: !!descriptionValue,
      })}
    >
      <div className="edit-mode-activate">
        <Tooltip placement="top" title={tooltipMessage || null}>
          <div>
            <Form.Item noStyle name={nameField} preserve>
              <InputNumber
                controls={false}
                defaultValue={isMaxBooking ? undefined : 0}
                disabled={disabled || isPassed(date)}
                min={0}
              />
            </Form.Item>
          </div>
        </Tooltip>
        {hasDuplication &&
          !disabled &&
          handleDuplicateHover &&
          dropDuplicationHover &&
          handleDuplicate && (
            <DuplicationMenu
              date={date}
              currentRange={{
                start: formTimePeriod.startDate,
                end: formTimePeriod.endDate,
              }}
              tryDuplicate={handleDuplicate}
              handleDuplicateHover={handleDuplicateHover}
              dropDuplicationHover={dropDuplicationHover}
            />
          )}
      </div>
      {descriptionValue !== undefined && (
        <span className="description">{descriptionValue}</span>
      )}
      {error && nameField && form?.isFieldTouched(nameField) && (
        <div className="error-message">{error}</div>
      )}
      {!!iconParameters && (
        <CellIcon
          iconColor={iconParameters.iconColor}
          showIcon={iconParameters.showIcon}
          iconType={iconParameters.iconType}
          autoCell={iconParameters.autoCell}
          openingInLastMinuteAutoDiscountRange={
            iconParameters.openingInLastMinuteAutoDiscountRange
          }
        />
      )}
    </div>
  );
};
